import React, { useEffect } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Radio,
  Row,
} from 'antd';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/th_TH';
import { useMutation } from '@tanstack/react-query';
import { IDataModal } from '@/interfaces/dataModal';
import { IPriceSales } from '@/interfaces/price-sales';
import { addPriceSales, editPriceSales } from '@/adapters/price-sales';
import ProductSelector from '@/components/selector/product-finder-selector';
import { IProduct } from '@/interfaces/product';

interface IProps {
  customerId: number;
  dataModal?: IDataModal<Partial<IPriceSales>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const PriceSalesModal = ({
  customerId,
  dataModal,
  handleOk,
  handleCancel,
}: IProps) => {
  const [form] = Form.useForm<Partial<IPriceSales>>();
  const mutations = useMutation({
    mutationFn: async (data: Partial<IPriceSales>) =>
      dataModal?.id ? await editPriceSales(data) : await addPriceSales(data),
    mutationKey: ['action-price-sales'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        const message = dataModal?.id
          ? 'อัพเดท Price Sales สำเร็จ'
          : 'สร้าง Price Sales สำเร็จ';
        notification.success({
          message: message,
        });
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IPriceSales>) => {
        values.id = Number(dataModal?.id || 0);
        values.customerId = customerId;
        values.validFrom = values.validFrom && dayjs(values.validFrom).toDate();
        values.validTo = values.validTo && dayjs(values.validTo).toDate();
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (dataModal?.id) {
      const item = dataModal?.item;
      form.setFieldsValue({
        ...item,
        validFrom: (item?.validFrom && dayjs(item.validFrom)) ?? null,
        validTo: (item?.validTo && dayjs(item.validTo)) ?? null,
      });
    }
  }, [dataModal?.id, dataModal?.item, form]);

  const onSelectProduct = (item: IProduct) => {
    form.setFieldsValue({
      productId: item.id,
      productCode: item.code,
      productName: item.name,
      productUnitPrice: item.unitPrice,
    });
  };

  return (
    <Modal
      title={'Price Sales'}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="priceSales"
          loading={mutations.isPending}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="priceSales"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
          initialValues={{
            validFrom: dayjs(),
          }}
        >
          <Form.Item name={'productId'} hidden />
          <Row gutter={12}>
            <Col span={24}>
              <div className="flex w-full flex-row items-end gap-3">
                <div className="flex w-full">
                  <Form.Item
                    label="สินค้า"
                    name="productName"
                    rules={[{ required: true, message: 'ระบุสินค้า' }]}
                    className="w-full"
                  >
                    <Input
                      size="large"
                      readOnly
                      placeholder="ค้นหาสินค้าจากด้านขวา"
                    />
                  </Form.Item>
                </div>
                <div className="flex">
                  <Form.Item label="" name="">
                    <ProductSelector
                      handleSelectData={product => {
                        onSelectProduct(product);
                      }}
                      size="large"
                    />
                  </Form.Item>
                </div>
              </div>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item<IPriceSales>
                label="Base Unit Price"
                name="productUnitPrice"
              >
                <InputNumber
                  disabled
                  readOnly
                  className="w-full"
                  size="large"
                  placeholder="ราคาขาย"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item<IPriceSales>
                label="ราคาขาย"
                name="unitPrice"
                rules={[{ required: true, message: 'ระบุราคาขาย' }]}
              >
                <InputNumber
                  className="w-full"
                  size="large"
                  placeholder="ราคาขาย"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item<IPriceSales> label="เลขที่ใบเสนอราคา (QT)" name="qtNo">
                <Input size="large" placeholder="เลขที่ใบเสนอราคา (QT)" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<Partial<IPriceSales>>
                label="Valid from"
                name="validFrom"
              >
                <DatePicker size="large" className="w-full" locale={locale} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item<Partial<IPriceSales>> label="Valid to" name="validTo">
                <DatePicker size="large" className="w-full" locale={locale} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                label="สถานะ"
                name="isActive"
                rules={[{ required: true, message: 'เลือกสถานะ' }]}
                initialValue={true}
              >
                <Radio.Group>
                  <Radio value={true}>ใช้งาน</Radio>
                  <Radio value={false}>ไม่ใช้งาน</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default PriceSalesModal;
